import React, { useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { CertificateOutletType } from '..'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ParamType } from '../CreateParam'
import ViewParamModal from '../../../../components/Certificates/ViewParamModal'
import { Copy, Eye, Pencil } from 'lucide-react'
import { matchAllFilters } from '../../../../utils/filterMatcher'
import { StandardColumn, StandardDataTable, useColumnsState } from '../../../../components/StandardPage'

const ParamTableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const paramsScreenKey = 'certificate_params'

const buildParamColumns = (): StandardColumn<ParamType>[] => [
  { id: 'name', label: 'Nome do parâmetro' },
  { id: 'class', label: 'Classe' },
  { id: 'family', label: 'Família' },
  { id: 'astm', label: 'ASTM' },
  { id: 'uns', label: 'UNS' },
  { id: 'din', label: 'DIN' },
  { id: 'aisi', label: 'AISI' },
  { id: 'others', label: 'Outras' },
]

const ParamTable = () => {
  const [parameters, setParameters] = useState<ParamType[]>([])
  const [filteredParameters, setFilteredParameters] = useState<ParamType[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [viewParamId, setViewParamId] = useState<number | null>(null)
  const { appliedFilters, search, refresh, setLoadingHeader } = useOutletContext<CertificateOutletType>()
  const navigate = useNavigate()

  const columns = useMemo(() => buildParamColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(paramsScreenKey, columns)

  const fetchParams = () => {
    setLoadingHeader?.(true)
    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param`)
      .then(response => {
        if(response.data) {
          setParameters(response.data)
          setFilteredParameters(response.data)
        }
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoadingHeader?.(false)
      })
  }

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (refresh) fetchParams()
  }, [refresh])

  useEffect(() => {
    fetchParams()
  }, [])

  useEffect(() => {
    let filtered = [...parameters]

    if (search && search.trim()) {
      filtered = filtered.filter(param =>
        param.name?.toLowerCase().includes(search.toLowerCase()) ||
        param.class?.toLowerCase().includes(search.toLowerCase()) ||
        param.family?.toLowerCase().includes(search.toLowerCase()) ||
        param.astm?.toLowerCase().includes(search.toLowerCase()) ||
        param.uns?.toLowerCase().includes(search.toLowerCase()) ||
        param.din?.toLowerCase().includes(search.toLowerCase()) ||
        param.aisi?.toLowerCase().includes(search.toLowerCase()) ||
        param.others?.toLowerCase().includes(search.toLowerCase())
      )
    }

    if (appliedFilters.length > 0) {
      filtered = filtered.filter(param => matchAllFilters(param as unknown as Record<string, any>, appliedFilters))
    }

    setFilteredParameters(filtered)
  }, [parameters, appliedFilters, search])

  return (
    <ParamTableRoot>
      <StandardDataTable
        columns={columns}
        data={filteredParameters.slice(0, visibleCount)}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={filteredParameters.length}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        hasMore={visibleCount < filteredParameters.length}
        countLabel='parâmetros'
        exportFileName='parametros'
        emptyMessage='Nenhum parâmetro encontrado.'
        rowActions={(param) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => param.id && setViewParamId(param.id) },
          { label: 'Duplicar', icon: <Copy size={16} />, onClick: () => param.id && navigate(`create?duplicateFrom=${param.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`edit/?id=${param.id}`) },
        ]}
      />
      {viewParamId !== null && (
        <ViewParamModal
          paramId={viewParamId}
          onClose={() => setViewParamId(null)}
        />
      )}
    </ParamTableRoot>
  )
}

export default ParamTable
